// Customer portal — shared types.
//
// Each section under `./sections/` exports a `CustomerSection` object and
// is mounted by `./main.ts` into a vertical stack on the project page.
// Sections do NOT know about each other; they communicate only via the
// `SectionContext` and the immutable `CustomerProject` data.

import { api } from "@tkos/api-client";

/** Toast severity. */
export type ToastKind = "info" | "success" | "error";

/** Retailer branding pulled by the chrome on load. */
export interface RetailerBranding {
  id: string;
  businessName: string;
  logoUrl: string | null;
  primaryColour: string;
  secondaryColour: string;
  /**
   * Retailer's personal phone line. **The customer portal MUST NOT
   * display this.** It's kept on the type so the API contract round-
   * trips, but every customer-facing "call us / need help" surface
   * must read `twilioNumber` instead — the API endpoint at
   * GET /api/customer/project omits this field on the wire, so it
   * will normally be null. Defraud-sacred per
   * [[feedback-contact-masking]].
   */
  phone: string | null;
  email: string | null;
  /**
   * Platform-allocated Twilio number — the ONLY phone number that
   * may be shown to customers. Routes inbound through TKOS so the
   * retailer's personal line stays private.
   */
  twilioNumber: string | null;
  /** Direct URL to the retailer's Google Business Profile review page,
   * e.g. `https://search.google.com/local/writereview?placeid=XXX`. Null
   * if the retailer hasn't connected their GBP yet — review prompt
   * section hides itself in that case. */
  googleReviewUrl: string | null;
}

/** The customer this portal session represents. */
export interface CustomerIdentity {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  address: unknown;  // jsonb — format with formatAddress() before display
  postcode?: string; // stored separately from address (Issue 7)
  /** ISO timestamp the customer accepted the T&C, or null until they do. Drives
   *  the first-open Terms & Conditions gate. */
  termsAcceptedAt?: string | null;
}

/** Project state: what stage the customer's kitchen is at. */
export type ProjectStatus =
  | "lead"
  | "qualified"
  | "quoted"
  | "approved"
  | "in_progress"
  | "delivered"
  | "installed"
  | "complete";

/** One stage of the live kitchen journey (Bug 4). Derived server-side from
 *  the CRM stage, deliveries and calendar; rendered as a horizontal progress
 *  bar on the Home screen. */
export interface JourneyStage {
  key: string;
  label: string;
  state: "done" | "current" | "upcoming";
}

/** The customer's journey across their kitchen project. `supplyType` controls
 *  which stages appear (supply-only omits Installation). */
export interface ProjectJourney {
  supplyType: "supply_install" | "supply_only";
  currentKey: string;
  stages: JourneyStage[];
}

/** A single quote line item, simplified for customer display (no trade prices). */
export interface CustomerQuoteItem {
  id: string;
  description: string;
  unit: string | null;
  quantity: number;
  lineTotal: number;   // retail GBP including margin (NEVER trade prices)
  roomName: string | null;
}

/** Customer-visible quote. */
export interface CustomerQuote {
  id: string;
  reference: string;   // KOS-Q-XXXX
  status: "draft" | "sent" | "approved" | "rejected" | "expired";
  projectName: string | null;
  rooms: Array<{ id: string; name: string; subtotal: number }>;
  items: CustomerQuoteItem[];
  subtotal: number;
  vatAmount: number;
  totalIncVat: number;
  deliveryCharge: number;
  validDays: number;
  sentAt: string | null;
  approvedAt: string | null;
  expiresAt: string | null;
  termsConditions: string | null;
  /** Retailer's VAT registration number — shown on the quote where legally
   *  required. Null when the retailer isn't VAT registered. */
  retailerVatNumber: string | null;
  /** Cover note shown above the line items, and closing note shown below the
   *  totals (above T&Cs). Captured into the snapshot at send time. */
  introNote: string | null;
  closingNote: string | null;
  /** Two-party document header captured at send (seller = retailer, buyer =
   *  customer). Null on older quotes sent before this feature. */
  header: QuoteHeader | null;
  /** Retailer's BACS details for paying by bank transfer.
   *
   *  The SERVER only sends this on a quote the customer has ACCEPTED (Steve,
   *  2026-09-11: bank details must never appear on a quote still being
   *  considered), so the client does not need to re-check acceptance — if it
   *  is present, it is meant to be shown. Null otherwise, including for
   *  retailers who have not entered any details.
   *
   *  Display-only: the platform does not process or reconcile bank transfers.
   *  Card payments go through Stripe, elsewhere in the portal. */
  bankDetails: QuoteBankDetails | null;
}

/** Retailer BACS details, as displayed to a customer on an accepted quote. */
export interface QuoteBankDetails {
  accountName: string;
  sortCode: string;
  accountNumber: string;
  /** Retailer-authored note on what payment reference to use. May be empty. */
  referenceNote: string;
}

/** Address-header blocks for a customer-facing quote. */
export interface QuoteHeader {
  seller: {
    name: string;
    addressLines: string[];
    phone: string | null;
    email: string | null;
    vatNumber: string | null;
  };
  buyer: {
    name: string;
    addressLines: string[];
  };
}

/** One stage of the staged payment schedule. */
export interface PaymentStage {
  id: string;
  stageName: string;       // "Deposit", "Pre-delivery", "Final payment"
  percentage: number;
  amount: number;          // GBP
  triggerEvent: "on_order" | "before_delivery" | "on_completion" | string;
  dueDate: string | null;
  paidAt: string | null;
  status: "pending" | "sent" | "paid" | "overdue";
  stripePaymentIntentId: string | null;
}

/** A design file shared with the customer. */
export interface DesignFile {
  id: string;
  filename: string;
  fileType: string | null;
  fileUrl: string;
  fileSize: number | null;
  notes: string | null;
  uploadedAt: string;
}

/** Geographic point (WGS84). */
export interface GeoPoint {
  lat: number;
  lng: number;
}

/** Live driver telemetry (only present when a TKOS driver is en route). */
export interface DriverLocation extends GeoPoint {
  driverName: string | null;
  /** ISO 8601 — when the position was last reported. */
  updatedAt: string;
  /** Optional heading in degrees clockwise from north. */
  heading: number | null;
}

/** Delivery tracking entry. */
export interface CustomerDelivery {
  id: string;
  kosReference: string;
  description: string | null;
  carrier: string | null;
  trackingNumber: string | null;
  eta: string | null;
  status: "scheduled" | "in_transit" | "out_for_delivery" | "delivered" | "failed";
  events: Array<{ status: string; description: string | null; at: string }>;
  /** Live driver telemetry. Only TKOS-driver deliveries carry this; courier deliveries leave it null. */
  driverLocation: DriverLocation | null;
  /** Destination point (customer address geocoded to lat/lng). Null if not yet geocoded. */
  destination: GeoPoint | null;
}

/** A single message in the customer↔retailer thread. */
export interface CustomerMessage {
  id: string;
  direction: "inbound" | "outbound";  // outbound = from customer to retailer
  subject: string | null;
  body: string;
  channel: "email" | "sms" | "whatsapp" | "phone" | "in_app";
  sentAt: string;
  read: boolean;
}

/** One warranty-registered appliance / item. */
export interface WarrantyItem {
  id: string;
  itemType: string;          // "Oven", "Hob", "Dishwasher", "Cabinetry"…
  manufacturer: string | null;
  modelNumber: string | null;
  serialNumber: string | null;
  /** ISO date — when the warranty period starts (usually install completion date). */
  warrantyStartsAt: string | null;
  /** Length of the warranty in months. */
  warrantyMonths: number | null;
  notes: string | null;
  registeredAt: string;
}

/** A booking / appointment between customer and retailer. */
export interface CustomerBooking {
  id: string;
  visitType: "showroom" | "home_visit" | "callback" | "design_consultation" | "site_survey" | string;
  /** ISO date "YYYY-MM-DD". Server may also accept full ISO timestamps. */
  preferredDate: string;
  /** "HH:MM" 24h, optional. */
  preferredTime: string | null;
  notes: string | null;
  status: "pending" | "confirmed" | "completed" | "cancelled";
  requestedAt: string;
  confirmedAt: string | null;
  /** Server-confirmed slot (set when the retailer confirms). */
  scheduledAt: string | null;
}

/** Complete project view aggregated by the chrome on load. */
export interface CustomerProject {
  customer: CustomerIdentity;
  retailer: RetailerBranding;
  status: ProjectStatus;
  /** Live journey (Bug 4) for the Home progress bar. Null when the backend
   *  can't derive it (no CRM link); the bar then doesn't render. */
  journey: ProjectJourney | null;
  projectName: string | null;
  installDate: string | null;
  /** The most-recent quote (latest). Kept for single-quote consumers
   *  (project-overview, payments, AI assistant). */
  quote: CustomerQuote | null;
  /** Every quote the customer has been sent (Item 8), newest-first. The Quote
   *  section renders all of these. */
  quotes: CustomerQuote[];
  paymentSchedule: PaymentStage[];
  designs: DesignFile[];
  deliveries: CustomerDelivery[];
  messages: CustomerMessage[];
  /** Unread inbound message count, computed server-side from unified messaging
   *  (project.messages is not populated with live messages). Drives the home
   *  "New messages" card. */
  unreadMessages?: number;
  /** Global customer-render feature flag (from /api/customer/project). When
   *  false, the designs section hides all AI-render content. */
  customerRenderEnabled?: boolean;
  // documents share the same shape as designs (file_url, filename, notes); kept separate
  // for clarity even though backend may unify them.
  documents: DesignFile[];
  bookings: CustomerBooking[];
  warrantyItems: WarrantyItem[];
}

/** What each section receives. */
export interface SectionContext {
  /** Typed fetch helper (same as retailer's). */
  api: typeof api;
  /** Read-only snapshot of the customer's project. */
  project: CustomerProject;
  /** Show a toast in the chrome's toast region. */
  showToast: (msg: string, kind?: ToastKind) => void;
  /** Re-fetch the project from the server (e.g. after an approval or payment). */
  refresh: () => Promise<void>;
}

/** Cleanup callback returned by `mount`. */
export type SectionCleanup = () => void;

/** A pluggable section of the customer portal. */
export interface CustomerSection {
  id: string;
  /** Section heading shown above the content (optional). */
  label?: string;
  /** Decides whether to render this section for the given project state. */
  shouldRender(project: CustomerProject): boolean;
  /** Render into `container`; return cleanup. */
  mount(container: HTMLElement, ctx: SectionContext): SectionCleanup;
}

/** Address formatter — handles all observed shapes (object/string/null/array). */
export function formatAddress(addr: unknown): string {
  if (addr == null) return "—";
  if (typeof addr === "string") {
    const t = addr.trim();
    // Legacy poisoned string from earlier broken stringify — treat as empty.
    if (t === "" || t === "[object Object]") return "—";
    return t;
  }
  if (typeof addr !== "object") return "—";
  if (Array.isArray(addr)) return "—";
  const a = addr as Record<string, unknown>;
  const parts = (["line1", "line2", "city", "county", "postcode"] as const)
    .map((k) => a[k])
    .filter((v): v is string => typeof v === "string" && v.trim() !== "")
    .map((v) => v.trim());
  return parts.length > 0 ? parts.join(", ") : "—";
}
